import { io } from 'socket.io-client';

class WebSocketService {
    constructor() {
        this.socket = null;
        this.listeners = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
    }

    connect() {
        if (this.socket?.connected) {
            return this.socket;
        }

        // Get auth token from store
        const authStore = JSON.parse(localStorage.getItem('auth-storage') || '{}');
        const token = authStore?.state?.token;
        const userType = authStore?.state?.userType;

        // Only connect for admin users
        if (userType !== 'admin' || !token) {
            console.log('WebSocket connection skipped - not an admin user');
            return null;
        }

        // Use the current domain for WebSocket connection
        const socketURL = window.location.origin;

        this.socket = io(socketURL, {
            auth: {
                token
            },
            transports: ['websocket', 'polling'],
            upgrade: true,
            rememberUpgrade: true,
            timeout: 20000,
            forceNew: false,
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: this.reconnectDelay,
            reconnectionDelayMax: 5000,
            maxHttpBufferSize: 1e8
        });

        this.setupEventHandlers();
        return this.socket;
    }

    setupEventHandlers() {
        if (!this.socket) return;

        this.socket.on('connect', () => {
            console.log('WebSocket connected:', this.socket.id);
            this.reconnectAttempts = 0;

            // Re-register all listeners after reconnection
            this.listeners.forEach((callback, event) => {
                this.socket.on(event, callback);
            });
        });

        this.socket.on('disconnect', (reason) => {
            console.log('WebSocket disconnected:', reason);

            if (reason === 'io server disconnect') {
                // Server disconnected, try to reconnect
                this.socket.connect();
            }
        });

        this.socket.on('connect_error', (error) => {
            console.error('WebSocket connection error:', error);
            this.reconnectAttempts++;

            if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                console.error('Max reconnection attempts reached');
                this.socket.disconnect();
            }
        });

        this.socket.on('reconnect', (attemptNumber) => {
            console.log('WebSocket reconnected after', attemptNumber, 'attempts');
        });

        this.socket.on('reconnect_error', (error) => {
            console.error('WebSocket reconnection error:', error);
        });

        this.socket.on('reconnect_failed', () => {
            console.error('WebSocket reconnection failed');
        });
    }

    on(event, callback) {
        if (!this.socket) {
            this.connect();
        }

        if (this.socket) {
            this.socket.on(event, callback);
            this.listeners.set(event, callback);
        }
    }

    off(event, callback) {
        if (this.socket) {
            this.socket.off(event, callback);
            this.listeners.delete(event);
        }
    }

    emit(event, data) {
        if (this.socket?.connected) {
            this.socket.emit(event, data);
        } else {
            console.warn('WebSocket not connected, cannot emit:', event);
        }
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.listeners.clear();
            console.log('WebSocket disconnected');
        }
    }

    isConnected() {
        return this.socket?.connected || false;
    }

    // Admin-specific event handlers
    onUserCreated(callback) {
        this.on('user-created', callback);
    }

    onUserUpdated(callback) {
        this.on('user-updated', callback);
    }

    onUserStatusChanged(callback) {
        this.on('user-status-changed', callback);
    }

    onUserDeleted(callback) {
        this.on('user-deleted', callback);
    }

    // Remove admin event handlers
    offUserCreated(callback) {
        this.off('user-created', callback);
    }

    offUserUpdated(callback) {
        this.off('user-updated', callback);
    }

    offUserStatusChanged(callback) {
        this.off('user-status-changed', callback);
    }

    offUserDeleted(callback) {
        this.off('user-deleted', callback);
    }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;